import { useCallback, useMemo, useState } from 'react';

import { useQuery } from 'react-query';
import {
  FundPerformancePeriodSet,
  FundPerformanceResponse,
  getFundPerformance,
} from 'src/services/fidcComparator';

import { FundPerformancePeriodKey, SelectedFund } from './types';

export function useFundPerformance(
  selectedFunds: SelectedFund[] | undefined,
  baseMonth: string | undefined,
) {
  const operationIds = useMemo(
    () => selectedFunds?.map((f) => f.operationId) ?? [],
    [selectedFunds],
  );

  const {
    data: fundPerformanceData,
    isLoading: isFundPerfLoading,
    isError: isFundPerfError,
  } = useQuery<FundPerformanceResponse>(
    ['fund-performance', baseMonth, operationIds.map(String).join(',')],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => getFundPerformance(baseMonth!, operationIds),
    {
      enabled: Boolean(baseMonth) && operationIds.length > 0,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const [pddPeriod, setPddPeriod] =
    useState<FundPerformancePeriodKey>('12_months');
  const [dcPeriod, setDcPeriod] =
    useState<FundPerformancePeriodKey>('12_months');

  const buildSeries = useCallback(
    (periodSet: FundPerformancePeriodSet, key: FundPerformancePeriodKey) => {
      const items = periodSet[key]?.items ?? [];
      const order = new Map(
        (selectedFunds ?? []).map((f, i) => [String(f.operationId), i]),
      );
      return [...items]
        .sort(
          (a, b) =>
            (order.get(a.operationId) ?? 999) -
            (order.get(b.operationId) ?? 999),
        )
        .map((item) => ({
          name: item.name,
          data: item.periods.map(({ period, value }) => ({
            x: period,
            y: value,
          })),
        }));
    },
    [selectedFunds],
  );

  const pddSeries = useMemo(
    () =>
      fundPerformanceData
        ? buildSeries(fundPerformanceData.pddOverDelay, pddPeriod)
        : [],
    [fundPerformanceData, pddPeriod, buildSeries],
  );

  const dcSeries = useMemo(
    () =>
      fundPerformanceData
        ? buildSeries(fundPerformanceData.dcOverPl, dcPeriod)
        : [],
    [fundPerformanceData, dcPeriod, buildSeries],
  );

  return {
    isFundPerfLoading,
    isFundPerfError,
    pddSeries,
    dcSeries,
    pddPeriod,
    setPddPeriod,
    dcPeriod,
    setDcPeriod,
  };
}
